import { Controller, useFormContext } from "react-hook-form";
import { MUTATION_UPDATE_LOCATION } from "../../common";
import { useMutation } from "@apollo/client";
import { Location } from "@towersystems/roam-common/lib/generated-types";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Page,
  Stack,
  useFlashMessages,
  TextField,
  FlatActionList,
  Divider,
} from "@towersystems/roam-common-native";
import { Colors, View } from "react-native-ui-lib";
import { LocationFormValues } from "../../components/LocationForm";
import { LocationFormParamList } from "../../../../types";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Switch } from "react-native";

export type LocationFormScreenProps = NativeStackScreenProps<
  LocationFormParamList,
  "LocationFormScreen"
>;

export const LocationFormScreen = ({ navigation }: LocationFormScreenProps) => {
  const { control, formState, handleSubmit, watch } =
    useFormContext<LocationFormValues>();

  const id = watch("id");
  const flashMessagesContext = useFlashMessages();
  const title = id ? "Edit Location" : "Create Location";

  const { t } = useTranslation("settings");

  const [updateLocationFn] = useMutation<{ updateLocation: Location }>(
    MUTATION_UPDATE_LOCATION
  );

  const handleOnSubmit = (data: LocationFormValues) => {
    if (data.id) {
      updateLocationFn({
        variables: {
          input: {
            id: data.id,
            name: data.name,
            enabled: data.enabled,
            retailerLocationKey: data.retailerLocationKey,
          },
        },
      })
        .then((result) => {
          if (!result.data?.updateLocation) {
            throw new Error();
          }
          flashMessagesContext.showMessage({
            message: t("message.location_updated"),
            severity: "success",
          });
          navigation.goBack();
        })
        .catch((error) => {
          flashMessagesContext.showMessage({
            message: t("message.error_updating_location", { error }),
            severity: "error",
          });
        });
    } else {
      return; // not implemented yet
    }
  };

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_location")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={title}
      secondaryTitle="Manage location settings"
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="General">
          <Stack vertical>
            <Controller
              control={control}
              name="name"
              render={({ field: { onChange, value } }) => (
                <TextField label="Name" value={value} onChangeText={onChange} />
              )}
            />
            <Controller
              control={control}
              name="retailerLocationKey"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Retailer Location Key"
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "location_enabled",
                  title: t("form.location_enabled.label"),
                  subTitle: t("form.location_enabled.description"),
                  onPress: () => {},
                  componentRight: () => (
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};
